"use client";
import React, { useEffect, useRef } from "react";
import { getStrapiMedia, getStrapiURL } from "../lib/api-helpers";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import gsap from "gsap";
import Image from "next/image";

gsap.registerPlugin(ScrollTrigger);

interface items {
  title: string;
  icon: {
    data: {
      attributes: {
        name: string;
        url: string;
      };
    };
  };
  borderColor: string;
  backgroundColor: string;
  description: string;
}

interface List {
  data: {
    headingList: string;
    descriptionList?: string;
    isTextAligned: string;
    isGradient: boolean;
    banner: {
      data: {
        attributes: {
          name: string;
          url: string;
        };
      };
    };
    items: items[];
  };
}

function ListItem({ title, icon, description }: items) {
  const imgUrl = getStrapiMedia(icon.data?.attributes?.url);

  return (
    <div
      className={`container mx-auto gap-4 flex items-center justify-start w-full pl-[24px] pr-[36px] py-[28px]`}>
      <div className='flex items-center justify-center gap-5 mb-0 '>
        <Image
          className={`w-16 min-w-16 h-16 ${icon.data?.attributes?.name.includes('quick') ? 'xl:h-12' : 'xl:h-20'} aspect-square`}
          src={imgUrl ?? ""}
          alt={icon.data?.attributes?.name}
          width={80}
          height={80}
        />
      </div>
      <>
        {title && (
          <div
            className={`text-[#001630] !leading-[1.15] !text-lg font-semibold text-left mb-3`}
            dangerouslySetInnerHTML={{ __html: title }}
          />
        )}
        <div
          className={`border-card-description title-font text-2xl md:text-lg inline-block font-normal text-[#032d60] text-left`}
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </>
    </div>
  );
}

const ListSection = ({ data }: List) => {
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const imgUrl = getStrapiMedia(data?.banner?.data?.attributes?.url);
  useEffect(() => {
    gsap.fromTo(
      cardRefs.current,
      { y: 100, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        stagger: 0.25,
        duration: 0.8,

        scrollTrigger: {
          trigger: cardRefs.current,
          toggleActions: "restart none none reverse",
          start: "top 70%",
        },
      }
    );
  }, []);
  const getGradientClasses = (isGradient: boolean): string => {
    return isGradient ? 'bg-gradient-to-b from-white to-[#DBF2FF]' : 'bg-[#DBF2FF4D]';
  }

  return (
    <div
      className={`relative flex flex-col items-center justify-center py-[80px] lg:py-[120px] px-[24px] height-100 ${
        getGradientClasses(data.isGradient)
      } `}>
      <div className='container flex flex-col lg:flex-row justify-center items-center lg:gap-20 mx-auto'>
        <section className={`left-section relative  ${data.isGradient ? 'h-[360px] lg:h-auto' : ''} mb-[48px] lg:mb-0`}>
          <h2 className='sr-only'>List Section</h2>
            {
                imgUrl && 
                <Image
                className={`w-[360px] h-[360px]  xl:w-[400px] xl:h-[400px] 2xl:w-[480px] 2xl:h-[480px] aspect-square mx-auto  absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2`}
                src={imgUrl ?? ""}
                alt={data?.banner?.data?.attributes?.name}
                width={400}
                height={400}
              />
            }
          <div className={`max-w-[720px] xl:max-w-[750px] 2xl:max-w-[800px] 3xl:max-w-[850px] ${data.isGradient ? 'text-center md:w-10/12 mx-auto pt-[120px] lg:pt-20' : 'text-center lg:text-left'}`}>
              <div className='relative title-font !leading-[1.2] text-5xl 2xl:text-6xl mb-[48px] xl:mb-0 inline-block  font-bold text-[#032d60]'
              dangerouslySetInnerHTML={{ __html: data.headingList }} />
              {
                data.descriptionList && 
                <h6 className='relative title-font !leading-[1.45] text-2xl lg:text-lg  mb-4 inline-block font-normal text-[#032d60]'>
                  {data.descriptionList}
                </h6>
              }
          </div>
        </section>
        <section className='right-section flex items-center'>
          <h2 className='sr-only'>List Section</h2>
            <div className='max-w-[280px] md:max-w-[320px] lg:max-w-[420px] bg-[#75CCFF5C] rounded-t-[15px] rounded-bl-[30px] rounded-br-[15px] mx-auto relative'>
                <div className="list-items-container py-[24px] bg-[#FFF6] relative -top-[15px] md:-top-[30px] left-[15px] md:left-[30px] rounded-b-[15px] rounded-tr-[30px] rounded-tl-[15px]">
                    {data.items.map((item, index) => {
                        return (
                        <div
                            className="relative z-10 list-item-box"
                            ref={el => { cardRefs.current[index] = el; }}
                            key={index}>
                            <ListItem key={index} {...item} />
                        </div>
                        );
                    })} 
                </div>
            </div>
        </section>
      </div>
    </div>
  );
};

export default ListSection;
